import { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router, Route, Routes, useLocation,
} from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import ScrollToTop from './components/ScrollToTop';
import ScrollProgress from './components/ScrollProgress';
import WhatsAppButton from './components/WhatsAppButton';
import BackToTop from './components/BackToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import useAnalytics from './hooks/useAnalytics';

// Code-split page bundles so the initial paint stays lean.
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.25 } },
};

function AnimatedRoutes() {
  const location = useLocation();
  useAnalytics();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <Home />
            </motion.div>
          }
        />
        <Route
          path="/services"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <Services />
            </motion.div>
          }
        />
        <Route
          path="/projects"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <Projects />
            </motion.div>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <ProjectDetail />
            </motion.div>
          }
        />
        <Route
          path="/about"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <About />
            </motion.div>
          }
        />
        <Route
          path="/contact"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
              <Contact />
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-ink-900 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <ScrollProgress />
      <ScrollToTop />

      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main id="main" className="flex-grow">
          <Suspense fallback={<div className="min-h-[60vh]" />}>
            <AnimatedRoutes />
          </Suspense>
        </main>
        <Footer />
      </div>

      <WhatsAppButton />
      <BackToTop />
    </Router>
  );
}

export default App;
